import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { ADD_WARDROBE_ITEMS } from "@/graphql/mutations/wardrobe";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import { Upload } from "@/components/forms/Upload";

const WardrobeNew: React.FC = () => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const [addWardrobeItems] = useMutation(ADD_WARDROBE_ITEMS);
  const [creatingBatch, setCreatingBatch] = useState<{ current: number; total: number } | null>(null);

  // Called when Upload returns signed IDs, trigger mutation in batches
  const handleUpload = async (signedIds: string[]) => {
    if (!signedIds || signedIds.length === 0) {
      setError("Please upload at least one image.");
      return;
    }
    setUploading(true);
    setError(null);
    setSuccess(false);
    try {
      // Chunk into batches of 10 to avoid large payloads/timeouts
      const chunkSize = 10;
      const chunks: string[][] = [];
      for (let i = 0; i < signedIds.length; i += chunkSize) {
        chunks.push(signedIds.slice(i, i + chunkSize));
      }

      setCreatingBatch({ current: 0, total: chunks.length });
      const allErrors: string[] = [];

      for (let i = 0; i < chunks.length; i++) {
        setCreatingBatch({ current: i + 1, total: chunks.length });
        const { data } = await addWardrobeItems({ variables: { images: chunks[i] } });
        if (!data?.createWardrobeItems?.success) {
          const err =
            data?.createWardrobeItems?.errors?.map((e: { fullMessages: string[] }) => e.fullMessages.join(", ")).join("; ") ||
            "Upload failed.";
          allErrors.push(`Batch ${i + 1}: ${err}`);
        }
      }

      if (allErrors.length === 0) {
        setSuccess(true);
        setTimeout(() => navigate("/wardrobe", { state: { refetch: true } }), 1500);
      } else {
        setError(allErrors.join(" | "));
      }
    } catch (err: any) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      setCreatingBatch(null);
    }
  };

  return (
    <>
      <Breadcrumb
        title="Add to Wardrobe"
        trail={[
          { label: "Dashboard", to: "/" },
          { label: "Wardrobe", to: "/wardrobe" },
          { label: "Add New" },
        ]}
      />
      <div className="max-w-2xl mx-auto p-6">
        <h1 className="text-2xl font-bold mb-4">Add New Wardrobe Items</h1>
        <Upload
          multiple
          onUpload={handleUpload}
          label="Upload wardrobe item images"
        />
        {error && (
          <div className="text-red-500 mb-2">{error}</div>
        )}
        {success && (
          <div className="text-green-600 mb-2">Upload successful!</div>
        )}
        {uploading && (
          <div className="text-gray-500 mb-2">
            Uploading...
            {creatingBatch && (
              <span className="ml-2">Creating items {creatingBatch.current} / {creatingBatch.total}</span>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default WardrobeNew;
